// Enchant-effect application (MB.4-remainder). PURE (ENG-1): the consuming-system
// maths for the tool/mining enchants — Efficiency (mine speed), Unbreaking
// (durability sparing), Fortune (extra ore drops), Silk Touch (drop-self), and
// Mending (XP→repair) — plus the XP a mined block yields. breaktime.ts, drops.ts,
// and the host mining/durability/XP paths call these so every enchant behaviour
// is a unit-testable function rather than scattered special cases.

export interface Ench { id: string; lvl: number; }

/** Level of enchant `id` on a list (0 if absent). */
export function enchantLevelOf(enchants: readonly Ench[] | undefined, id: string): number {
  return enchants?.find((e) => e.id === id)?.lvl ?? 0;
}

/** Efficiency adds level² + 1 to a matching tool's mining speed (Java §mining). */
export function efficiencyBonus(level: number): number {
  return level > 0 ? level * level + 1 : 0;
}

// ── Ranged / trident enchants (E2, M17: bow + trident) ───────────────────────
/** Power scales an arrow's damage by +25% per level (Java §bow). */
export function powerDamageMultiplier(level: number): number {
  return level > 0 ? 1 + 0.25 * level : 1;
}
/** Flame (bow) sets arrows alight. */
export function flameArrows(level: number): boolean { return level > 0; }
/** Infinity (bow) fires without spending an arrow. */
export function infiniteArrows(level: number): boolean { return level > 0; }
/** Punch (bow) adds knockback to a hit — strength per level. */
export function punchKnockback(level: number): number { return level * 0.6; }
/** Impaling (trident) adds 2.5 damage per level against aquatic targets. */
export function impalingBonus(level: number, aquaticTarget: boolean): number {
  return aquaticTarget && level > 0 ? level * 2.5 : 0;
}

/**
 * Probability that a use spends one durability point. Unbreaking spares it:
 * tools/bows 1/(level+1); armour 0.6 + 0.4/(level+1) (Java's kinder armour curve).
 * `roll` (0..1) < this → durability is consumed.
 */
export function durabilityUseChance(level: number, isArmor = false): number {
  if (level <= 0) return 1;
  return isArmor ? 0.6 + 0.4 / (level + 1) : 1 / (level + 1);
}

/** Whether a use consumes durability, given an unbreaking level and a 0..1 roll. */
export function consumesDurability(level: number, roll: number, isArmor = false): boolean {
  return roll < durabilityUseChance(level, isArmor);
}

/**
 * Fortune ore-drop multiplier (Java `ore_drops`): a value in 1..(level+1),
 * weighted toward 1 — `max(0, randInt(level+2) − 1) + 1`. `roll` is 0..1.
 */
export function fortuneMultiplier(level: number, roll: number): number {
  if (level <= 0) return 1;
  const bounded = Math.min(0.999999, Math.max(0, roll));
  const i = Math.max(0, Math.floor(bounded * (level + 2)) - 1);
  return 1 + i;
}

export function hasSilkTouch(enchants: readonly Ench[] | undefined): boolean {
  return enchantLevelOf(enchants, 'silk_touch') > 0;
}

/**
 * Mending repairs a geared item from absorbed XP (Java: 2 durability per point),
 * capped by the item's current damage. Returns how much was repaired and the XP
 * left over (which then flows to the player's level).
 */
export function mendingRepair(xp: number, damage: number): { repaired: number; xpLeft: number } {
  if (xp <= 0 || damage <= 0) return { repaired: 0, xpLeft: Math.max(0, xp) };
  const repaired = Math.min(damage, xp * 2);
  const xpUsed = Math.ceil(repaired / 2);
  return { repaired, xpLeft: Math.max(0, xp - xpUsed) };
}

// ── XP dropped when a block is mined (PLR-5) ─────────────────────────────────
/** XP-yielding blocks → [min, max] orbs when broken by hand/pickaxe (not silk). */
export const BLOCK_XP: Record<string, readonly [number, number]> = {
  coal_ore: [0, 2], deepslate_coal_ore: [0, 2],
  redstone_ore: [1, 5], deepslate_redstone_ore: [1, 5],
  lapis_ore: [2, 5], deepslate_lapis_ore: [2, 5],
  diamond_ore: [3, 7], deepslate_diamond_ore: [3, 7],
  emerald_ore: [3, 7], deepslate_emerald_ore: [3, 7],
  nether_quartz_ore: [2, 5], nether_gold_ore: [0, 1],
  sculk: [1, 1], sculk_catalyst: [5, 5], spawner: [15, 43],
};

/**
 * XP a mined block yields. Silk-touched ores drop no XP (the ore block itself is
 * collected instead). `roll` (0..1) picks within the block's [min,max] range.
 */
export function blockBreakXp(block: string, silkTouched: boolean, roll: number): number {
  if (silkTouched) return 0;
  const range = BLOCK_XP[block];
  if (!range) return 0;
  const [min, max] = range;
  return min + Math.floor(Math.max(0, Math.min(0.999999, roll)) * (max - min + 1));
}
